import { ErrorBoundary } from '@sentry/react';
import { WithBacktrack } from 'components/common/withBacktrack';
import { WithIsOffline } from 'components/common/withIsOffline';
import { WithAuthenticatedProviders } from 'components/common/WithAuthenticatedProviders';
import { WithBlobFileStorage } from 'components/core/BlobFileStorage';
import { SomethingWentWrongPage } from 'components/core/SomethingWentWrongPage';
import { useLogoutSubscription } from 'components/core/WithLogout/useLogoutSubscription';
import { WithServiceWorkerStatusCheck } from 'contexts/withServiceWorkerStatusCheck';
import { AppNavigationProvider } from 'contexts/navigation';
import { IntlProviderWrapper } from 'intl/IntlProviderWrapper';
import React, { ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from 'services/auth0/react-auth0.spa';
import { getRouteConfig } from 'setup/navigation/routes';
import { WithProductFruits } from 'setup/productFruits';
import { ThemeProviderWrapper } from 'setup/theme';
import { TableStateContext } from 'contexts/withTable';
import { TableStatePreserver } from 'views/issueTable/tableView/TableState';
import { useCurrentUserChangesSubscription } from 'store/user';

interface AppWrapperProps {
  tableState: TableStatePreserver;
  children: ReactNode;
}

type AppRootProps = Omit<AppWrapperProps, 'children'>;

/** App-wide notices from the service worker that need the auth context. */
function AppSubscriptions(): null {
  useLogoutSubscription();
  return null;
}

function AppWrapper({
  tableState,
  children,
}: AppWrapperProps): React.ReactElement {
  return (
    <WithIsOffline>
      <TableStateContext.Provider value={tableState}>
        <AppNavigationProvider>
          <AuthProvider>
            <IntlProviderWrapper>
              <ThemeProviderWrapper>
                <WithProductFruits>
                  <WithAuthenticatedProviders>
                    <WithServiceWorkerStatusCheck />
                    <AppSubscriptions />
                    <ErrorBoundary fallback={<SomethingWentWrongPage />}>
                      <WithBacktrack>
                        <WithBlobFileStorage>{children}</WithBlobFileStorage>
                      </WithBacktrack>
                    </ErrorBoundary>
                  </WithAuthenticatedProviders>
                </WithProductFruits>
              </ThemeProviderWrapper>
            </IntlProviderWrapper>
          </AuthProvider>
        </AppNavigationProvider>
      </TableStateContext.Provider>
    </WithIsOffline>
  );
}

export function AppRoot({
  tableState,
}: AppRootProps): React.ReactElement {
  useCurrentUserChangesSubscription();

  const routes = getRouteConfig().map((route) => ({
    ...route,
    element: (
      <AppWrapper tableState={tableState}>
        {route.element}
      </AppWrapper>
    ),
  }));

  const router = createBrowserRouter(routes);

  return <RouterProvider router={router} />;
}
